import { useState, useEffect, useMemo } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import { useContactList } from './useContactList';
import { useUserDirectorySearch } from './useUserDirectorySearch';
import { generateInitialsAvatar } from '../utils/avatar';

/**
 * Hook for managing a unified selectable user list for room creation/editing.
 * Combines known contacts with user directory search results.
 * Users are selectable without requiring DM rooms to exist.
 * 
 * @returns {{
 *   users: Array<{id: string, name: string, avatar: string, isKnownContact: boolean}>,
 *   searchTerm: string,
 *   setSearchTerm: (term: string) => void,
 *   isSearching: boolean,
 *   error: string | null
 * }}
 */
export const useSelectableUserList = () => {
  const client = useMatrixClient();
  const [searchTerm, setSearchTerm] = useState('');
  const { contactList } = useContactList();
  const { searchUsers, results: searchResults, isSearching, error } = useUserDirectorySearch();

  const myUserId = client?.getUserId();

  // Debounced user directory search
  useEffect(() => {
    const trimmedSearch = searchTerm.trim();
    
    // Only search if term is at least 2 characters
    if (trimmedSearch.length >= 2) {
      const timer = setTimeout(() => {
        searchUsers(trimmedSearch, 20);
      }, 300); // 300ms debounce

      return () => clearTimeout(timer);
    }
  }, [searchTerm, searchUsers]);

  /**
   * Convert contact list to standardized user format
   * contactList items have: {id, name, avatar: string}
   */
  const knownUsers = useMemo(() => {
    return contactList
      .filter(contact => contact.id !== myUserId)
      .map(contact => ({
        id: contact.id,
        name: contact.name,
        avatar: contact.avatar || generateInitialsAvatar(contact.name), // avatar is already a string URL or initials
        organization: contact.organization || '',
        lastActivityTimestamp: contact.lastActivityTimestamp || null,
        presence: contact.presence || null,
        online: contact.online || false,
        powerLevel: contact.powerLevel || 0,
        isKnownContact: true,
      }));
  }, [contactList, myUserId]);

  /**
   * Convert search results to standardized user format
   * Search results have: {user_id, display_name, avatar_url}
   */
  const searchResultUsers = useMemo(() => {
    return searchResults.map(user => {
      // Get MXC URL if avatar exists and convert to HTTP
      const mxcUrl = user.avatar_url;
      let avatarUrl = '';
      if (mxcUrl && client) {
        avatarUrl = client.mxcUrlToHttp(mxcUrl, 96, 96, 'crop');
      }
      
      // Fallback to initials avatar if no avatar URL
      if (!avatarUrl) {
        avatarUrl = generateInitialsAvatar(user.display_name || user.user_id);
      }
      
      return {
        id: user.user_id,
        name: user.display_name || user.user_id,
        avatar: avatarUrl,
        organization: '',
        lastActivityTimestamp: null,
        presence: null,
        online: false,
        powerLevel: 0,
        isKnownContact: false, // Mark as search result
      };
    });
  }, [searchResults, client]);

  /**
   * Merge known users and search results, removing duplicates
   */
  const users = useMemo(() => {
    if (!searchTerm.trim()) {
      // No search: return only known contacts
      return knownUsers;
    }

    // Deduplicate: prioritize known contacts over search results
    const knownUserIds = new Set(knownUsers.map(u => u.id));
    const uniqueSearchResults = searchResultUsers.filter(u => !knownUserIds.has(u.id));

    // Combine and filter based on search term
    const combined = [...knownUsers, ...uniqueSearchResults];
    
    const lowerSearch = searchTerm.toLowerCase();
    return combined.filter(user => {
      const matchesName = user.name.toLowerCase().includes(lowerSearch);
      const matchesId = user.id.toLowerCase().includes(lowerSearch);
      return matchesName || matchesId;
    });
  }, [knownUsers, searchResultUsers, searchTerm]);

  return {
    users,
    searchTerm,
    setSearchTerm,
    isSearching,
    error,
  };
};

export default useSelectableUserList;
